
import { supabase } from '@/integrations/supabase/client';
import { Procurement, CreateProcurementInput } from '@/types/database.types';
import { createProcurementItems } from '../procurementItemService';
import { createProcurementCosts } from '../procurementCostsService';
import { createProcurementFinance } from '../procurementFinanceService';
import { createProcurementWarehouse } from '../procurementWarehouseService';
import { createProcurementQuality } from '../procurementQualityService';
import { logStatusChange } from '../procurementAuditService';
import { enrichProcurementWithDetails } from './enrichmentService';
import { extractRequestorInfo } from './userDataService';
import { sendNewProcurementNotification } from './notificationService';

const PROCUREMENT_SELECT_QUERY = `
  *, 
  requestor:requestor_id(*)
`;

/**
 * Create a new procurement with items and related data
 */
export const createProcurement = async (data: Partial<CreateProcurementInput>): Promise<Procurement> => {
  // Extract items and related data from main data
  const { requestor, items, costs, finance, ...dataForDb } = data;
  
  console.log('Creating new procurement with data:', dataForDb);
  console.log('Items to create:', items);
  console.log('Costs data:', costs);
  console.log('Finance data:', finance);
  
  // Start a transaction to create procurement and related records
  const { data: newProcurement, error } = await supabase
    .from('procurements')
    .insert([dataForDb as any])
    .select(PROCUREMENT_SELECT_QUERY)
    .single();

  if (error) {
    console.error("Error creating procurement:", error);
    throw error;
  }

  console.log('Procurement created successfully:', newProcurement);
  
  try {
    // Create related records
    const promises = [];
    
    // Create procurement costs with required price_per_unit
    if (costs) {
      const costsWithDefaults = {
        procurement_id: newProcurement.id,
        price_per_unit: costs.price_per_unit || 0,
        ...costs
      };
      promises.push(createProcurementCosts(costsWithDefaults));
    }
    
    // Create procurement finance
    if (finance) {
      promises.push(createProcurementFinance({
        procurement_id: newProcurement.id,
        ...finance
      }));
    }
    
    // Create procurement warehouse record
    promises.push(createProcurementWarehouse({
      procurement_id: newProcurement.id
    }));
    
    // Create procurement quality record
    promises.push(createProcurementQuality({
      procurement_id: newProcurement.id
    }));
    
    // Create procurement items if provided
    if (items && items.length > 0) {
      promises.push(createProcurementItems(newProcurement.id, items));
    }
    
    // Create initial audit log
    promises.push(logStatusChange(
      newProcurement.id,
      '',
      newProcurement.status,
      newProcurement.requestor_id || '',
      'Procurement created'
    ));
    
    await Promise.all(promises);
    console.log('All related records created successfully');
    
  } catch (relatedError) {
    console.error('Error creating related records:', relatedError);
    // If related records creation fails, delete the procurement to maintain consistency
    await supabase.from('procurements').delete().eq('id', newProcurement.id);
    throw new Error('Failed to create procurement related records. Procurement was rolled back.');
  }
  
  const enriched = await enrichProcurementWithDetails(newProcurement);
  
  const finalProcurement = {
    ...enriched,
    requestor: extractRequestorInfo(enriched.requestor),
    approver: enriched.finance?.approved_by ? extractRequestorInfo(enriched.finance.approved_by) : null,
    rejector: enriched.finance?.rejected_by ? extractRequestorInfo(enriched.finance.rejected_by) : null
  } as Procurement;

  // Send Telegram notification for new procurement
  await sendNewProcurementNotification(finalProcurement);
  
  return finalProcurement;
};

/**
 * Update procurement status with audit logging
 */
export const updateProcurementStatus = async (id: string, status: string, changedBy?: string): Promise<Procurement> => {
  console.log(`Updating procurement ${id} status to: ${status}`);
  
  // Get current procurement to log the old status
  const { data: currentProcurement } = await supabase
    .from('procurements')
    .select('status')
    .eq('id', id)
    .single();
  
  const { data, error } = await supabase
    .from('procurements')
    .update({ 
      status,
      previous_status: currentProcurement?.status
    })
    .eq('id', id)
    .select(PROCUREMENT_SELECT_QUERY)
    .single();

  if (error) {
    console.error(`Error updating procurement ${id} status:`, error);
    throw error;
  }

  console.log(`Successfully updated procurement ${id} status to ${status}`);
  
  // Log the status change
  if (changedBy && currentProcurement) {
    try {
      await logStatusChange(
        id,
        currentProcurement.status,
        status,
        changedBy,
        `Status changed from ${currentProcurement.status} to ${status}`
      );
    } catch (auditError) {
      console.error('Error logging status change:', auditError);
      // Don't fail the status update if audit logging fails
    }
  }
  
  const enriched = await enrichProcurementWithDetails(data);
  
  return {
    ...enriched,
    requestor: extractRequestorInfo(enriched.requestor),
    approver: enriched.finance?.approved_by ? extractRequestorInfo(enriched.finance.approved_by) : null,
    rejector: enriched.finance?.rejected_by ? extractRequestorInfo(enriched.finance.rejected_by) : null
  } as Procurement;
};
